import './globalGifts.scss';

import { Col, Row, Typography } from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ZLSection from '../../containers/zl-section/zl-section';


import BookingSummary from '../../components/booking-summary/booking-summary';
import Heading, { HeadingSize } from '../../components/heading/heading';

import { actionCreators as PaymentActions } from '../../store/Payments';

import { actionCreators as GiftVoucherActions } from '../../store/GiftVoucher';
import { actionCreators as PurchaseTypeActions, PurchaseTypes } from '../../store/PurchaseTypes';
import { actionCreators as currencyActions} from '../../store/Currencies';
import { actionCreators as SiteActions } from '../../store/Sites';


import GiftVoucherAmountSection from '../../page-sections/gift-voucher/gift-voucher-amount-section';
import GiftVoucherDetailsSection from '../../page-sections/gift-voucher/gift-voucher-details-section';

import { useIsMobile } from '../../hooks/useIsMobile';
import GlobalGiftVoucherHeadingSection from '../../page-sections/global-gift-voucher/global-gift-voucher-heading-section';
import CurrencyPickerSection from '../../page-sections/gift-voucher/currency-picker-section';
import useSiteChange from '../../hooks/useSiteChange';
import { IApplicationState } from '../../store';


export interface IGlobalGiftsPageProps {
}

const GlobalGiftsPage: React.FunctionComponent<IGlobalGiftsPageProps> = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isMobile } = useIsMobile();

    const { clearSite } = useSiteChange();

    //Initialise the globally available currencies we can sell global gift vouchers with
    //TODO might be a better place to store or init this stuff
    const currenciesLoading = useSelector((state: IApplicationState) => state.currencies.isLoading);
    const currenciesLoaded = useSelector((state: IApplicationState) => state.currencies.hasLoaded);
    const currenciesLoadFailed = useSelector((state: IApplicationState) => state.currencies.loadFailed);

    const selectedCurrency = useSelector((state: IApplicationState) => state.giftVoucher.selectedVoucherCurrency);

    const sitesLoading = useSelector((state: IApplicationState) => state.sites.sitesLoading);
    const sitesLoaded = useSelector((s: IApplicationState) => s.sites.sitesLoaded);
    const selectedSite = useSelector((state: IApplicationState) => state.sites.selectedSite);

    //load site data if not already done
    // TODO might be too aggressive
    useEffect(() => {
        console.log(`Global gifts page running initilisation logic / useEffect`);

        dispatch(PurchaseTypeActions.selectPurchaseType(PurchaseTypes.GlobalGiftVoucher));

        //clear any previous state that may have carried over from the regular venue based gift voucher flow
        dispatch(GiftVoucherActions.cancelGiftVoucher());
        dispatch(GiftVoucherActions.clearGiftVoucher()); // This is throwing a 500 on inital load.

        dispatch(SiteActions.loadSites());
        dispatch(SiteActions.loadGlobleSite());
        dispatch(PaymentActions.clearClientSecret());

    }, [dispatch]);

    useEffect(() => {
        // currently I never want a selected site to persist while on the global gift flow
        // Just makes it too dificult to prevent things from cross poluting settings/config/currencyType/etc
        // Would be possible to chase down all the edge cases and prevent needing this, will just take a lot of time and testing

        if (selectedSite) {
            console.log(`Global gift vouchers page clearing selected site to prevent data contamination between purchase flows`);
            clearSite();
        }

    }, [selectedSite, clearSite]);

    //use effect to load available global gift currency options
    useEffect(() => {
        if (!currenciesLoading && !currenciesLoaded && !currenciesLoadFailed) {
            console.log(`Global gift page triggering load of global currencies`);
            dispatch(currencyActions.loadGlobalPaymentCurrencies());
        }
    }, [dispatch, currenciesLoading, currenciesLoaded, currenciesLoadFailed]);

    //use effect to keep gift voucher config syncronised with the selected currency
    useEffect(() => {
        if (selectedCurrency) {
            console.log(`Global gift page triggering load of global gift voucher config for currency ${selectedCurrency.id}-${selectedCurrency.symbol}-${selectedCurrency.code}`);
            dispatch(GiftVoucherActions.getGiftVoucherConfig(true, undefined, selectedCurrency.id));
            dispatch(GiftVoucherActions.selectVoucherCurrency(selectedCurrency))
        }
    }, [dispatch, selectedCurrency]);

    const purchaseAmountSelectedUI = useSelector((state: IApplicationState) => state.giftVoucher.selectedGiftVoucherAmountUI) ?? false;

    const pageTitle = t('globalGiftVouchers.title');
    const pageDescription = t('globalGiftVouchers.header.subheading');
    
    const showCurrencyPicker = !sitesLoading && sitesLoaded;
    const showAmountPicker = showCurrencyPicker && selectedCurrency !== undefined;
    const showRecipientPicker = showAmountPicker && purchaseAmountSelectedUI;

    return (
        <React.Fragment>
            <Row justify="start" align="top" id="global-gifts-page">

                {/*Page heading and title*/}
                <Col xs={24} sm={24} md={24} lg={18}>
                    <ZLSection title={''} active={true} scrollTo={false}>
                        <Heading level={HeadingSize.One} >
                            {pageTitle}
                        </Heading>
                        <Typography.Text style={{ fontSize: '14px' }}>
                            {pageDescription}
                        </Typography.Text>
                    </ZLSection>
                </Col>

                {/* Main page ui elements and data input*/}
                <Col xs={24} sm={24} md={24} lg={18}>
                    <GlobalGiftVoucherHeadingSection active={true}/>
                    <CurrencyPickerSection active={showCurrencyPicker}/>
                    <GiftVoucherAmountSection active={showAmountPicker} scrollTo={true} isGlobal={true} />
                    <GiftVoucherDetailsSection active={showRecipientPicker} scrollTo={true} />
                </Col>

                 {/*BookingSummary - sidebar or bottom drawer depending on screen size */}
                <Col xs={24} sm={24} md={24} lg={6}>
                    <BookingSummary 
                        drawer={isMobile} 
                        affix={!isMobile} 
                        dynamicOffsetTop={0}
                    />
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default GlobalGiftsPage;
